import { getAccessToken as getCozeAccessToken } from '@/api/merchant-coze'
import { getAccessToken as getMerchantJwt } from '@/store/common/token'
import { getAdminId } from '@/store/common/username'
import router from '@/router'
import cozeAssistantIcon from '@/assets/imgs/coze-assistant.png'

const COZE_SDK_URL = 'https://lf-cdn.coze.cn/obj/unpkg/flow-platform/chat-app-sdk/1.2.0-beta.20/libs/cn/index.js'
const COZE_BOT_ID = '7646311072513228838'
// 悬浮按钮用 webpack 打包图；SDK 对话框内用公网绝对地址（iframe 无法加载相对路径）
const COZE_FLOAT_ICON_URL = cozeAssistantIcon
const COZE_SDK_ICON_URL = 'https://public-data-test.oss-cn-hangzhou.aliyuncs.com/merchant/upload/2026-06-08/d2929489ed2b7cf98b28103b945c53ad.png'
const FLOAT_BTN_ID = 'simbart-coze-float-btn'
const FLOAT_BTN_HIDDEN_CLASS = 'is-hidden'
const FLOAT_BTN_RIGHT = '20px'

function applyPressureObserverStub (targetWindow) {
  if (!targetWindow || targetWindow.__cozePressureObserverPatched) return
  targetWindow.__cozePressureObserverPatched = true

  function PressureObserverStub (callback) {
    this._callback = callback
  }
  PressureObserverStub.prototype.observe = function () {}
  PressureObserverStub.prototype.unobserve = function () {}
  PressureObserverStub.prototype.disconnect = function () {}
  PressureObserverStub.knownSources = ['cpu']
  targetWindow.PressureObserver = PressureObserverStub
}

function patchCozeIframes () {
  document.querySelectorAll('iframe').forEach(function (iframe) {
    try {
      if (iframe.contentWindow) {
        applyPressureObserverStub(iframe.contentWindow)
      }
    } catch (error) {
      void error
    }
  })
}

function setupCozeIframeWatcher () {
  if (window.__cozeIframeWatcherSetup) return
  window.__cozeIframeWatcherSetup = true

  const observer = new MutationObserver(function () {
    patchCozeIframes()
  })
  observer.observe(document.body, { childList: true, subtree: true })
  patchCozeIframes()
}

/**
 * Coze SDK 兼容补丁：在加载 SDK 前执行，避免控制台报错影响初始化。
 */
function patchCozeSdkCompat () {
  if (window.__cozeSdkCompatPatched) return
  window.__cozeSdkCompatPatched = true

  applyPressureObserverStub(window)
  setupCozeIframeWatcher()

  if (window.isSecureContext) return
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return

  const mediaDevices = navigator.mediaDevices || {}
  mediaDevices.enumerateDevices = function () {
    return Promise.resolve([])
  }
  mediaDevices.getUserMedia = function () {
    return Promise.reject(new DOMException('NotAllowedError', 'NotAllowedError'))
  }
  navigator.mediaDevices = mediaDevices

  if (navigator.getUserMedia) {
    navigator.getUserMedia = function (_constraints, _success, error) {
      if (error) error(new DOMException('NotAllowedError', 'NotAllowedError'))
    }
  }
}

let cozeChatClient = null
let cozeSdkLoading = false
let bootstrapScheduled = false
let cozeChatOpen = false
let cozeBoundAccountKey = ''

function getCozeAccountKey () {
  const token = getMerchantJwt()
  if (!token) return ''
  const adminId = getAdminId() || ''
  return adminId + ':' + token.slice(-32)
}

function shouldReinitCozeClient () {
  const accountKey = getCozeAccountKey()
  if (!accountKey) return false
  if (!cozeChatClient) return true
  return cozeBoundAccountKey !== accountKey
}

function destroyCozeChatClient () {
  if (cozeChatClient) {
    try {
      if (typeof cozeChatClient.hideChatBot === 'function') {
        cozeChatClient.hideChatBot()
      }
      if (typeof cozeChatClient.destroy === 'function') {
        cozeChatClient.destroy()
      }
    } catch (error) {
      void error
    }
  }

  cozeChatClient = null
  cozeBoundAccountKey = ''
  cozeChatOpen = false
  document.body.classList.remove('coze-chat-open')

  const btn = document.getElementById(FLOAT_BTN_ID)
  if (btn) {
    btn.classList.remove(FLOAT_BTN_HIDDEN_CLASS)
  }
}

function getCozeUserId () {
  const adminId = getAdminId()
  if (adminId) {
    return 'merchant_admin_' + adminId
  }
  const token = getMerchantJwt()
  if (token) {
    return 'merchant_token_' + token.slice(-16)
  }
  return 'merchant_guest'
}

async function fetchCozeToken () {
  if (!getMerchantJwt()) {
    return ''
  }
  return getCozeAccessToken()
}

function applyFloatingButtonStyles (btn) {
  btn.style.position = 'fixed'
  btn.style.top = '50%'
  btn.style.right = FLOAT_BTN_RIGHT
  btn.style.transform = 'translateY(-50%)'
  btn.style.zIndex = '10001'
  btn.style.width = '56px'
  btn.style.height = '56px'
  btn.style.padding = '0'
  btn.style.margin = '0'
  btn.style.border = 'none'
  btn.style.backgroundColor = 'transparent'
  btn.style.backgroundImage = 'url("' + COZE_FLOAT_ICON_URL + '")'
  btn.style.backgroundSize = 'contain'
  btn.style.backgroundPosition = 'center'
  btn.style.backgroundRepeat = 'no-repeat'
  btn.style.cursor = 'pointer'
  btn.style.display = 'block'
  btn.style.outline = 'none'
  btn.style.boxSizing = 'border-box'
  btn.style.visibility = 'visible'
  btn.style.opacity = '1'
  btn.style.pointerEvents = 'auto'
}

function hideSdkFloatingButton () {
  const sdkBtns = document.querySelectorAll('.coze-floating-button, [class*="coze-floating"]')
  sdkBtns.forEach(function (el) {
    if (el.id === FLOAT_BTN_ID) return
    el.style.setProperty('display', 'none', 'important')
    el.style.setProperty('visibility', 'hidden', 'important')
    el.style.setProperty('pointer-events', 'none', 'important')
  })

  const logos = document.querySelectorAll('img[alt="logo"]')
  logos.forEach(function (img) {
    const host = img.parentElement
    if (!host || host.id === FLOAT_BTN_ID) return
    const style = window.getComputedStyle(host)
    if (style.position === 'fixed') {
      host.style.setProperty('display', 'none', 'important')
    }
  })
}

function removeFloatingButton () {
  const btn = document.getElementById(FLOAT_BTN_ID)
  if (btn) btn.remove()
}

function setFloatingButtonVisible (visible) {
  const btn = document.getElementById(FLOAT_BTN_ID)
  if (!btn) return
  if (visible) {
    btn.classList.remove(FLOAT_BTN_HIDDEN_CLASS)
    document.body.classList.remove('coze-chat-open')
  } else {
    btn.classList.add(FLOAT_BTN_HIDDEN_CLASS)
    document.body.classList.add('coze-chat-open')
  }
}

function openCozeChat () {
  if (!cozeChatClient) return
  setFloatingButtonVisible(false)
  if (typeof cozeChatClient.showChatBot === 'function') {
    cozeChatClient.showChatBot()
  }
}

function handleFloatingButtonClick (e) {
  e.preventDefault()
  e.stopPropagation()
  setFloatingButtonVisible(false)

  if (shouldReinitCozeClient()) {
    destroyCozeChatClient()
  }

  loadCozeSDK().then(function () {
    openCozeChat()
  })
}

function createCustomFloatingButton () {
  let btn = document.getElementById(FLOAT_BTN_ID)
  if (!btn) {
    btn = document.createElement('button')
    btn.id = FLOAT_BTN_ID
    btn.type = 'button'
    btn.title = 'AI服务助手'
    btn.setAttribute('aria-label', 'AI服务助手')
    btn.addEventListener('click', handleFloatingButtonClick)
    document.body.appendChild(btn)
  }

  applyFloatingButtonStyles(btn)
  hideSdkFloatingButton()
  if (cozeChatOpen) {
    setFloatingButtonVisible(false)
  }
  return btn
}

function ensureFloatingButton () {
  if (!getMerchantJwt()) {
    removeFloatingButton()
    return null
  }
  return createCustomFloatingButton()
}

async function initCozeChatClient () {
  if (cozeChatClient || !window.CozeWebSDK) return

  let cozeToken = ''
  try {
    cozeToken = await fetchCozeToken()
  } catch (error) {
    void error
    return
  }
  if (!cozeToken) return

  try {
    cozeChatClient = new window.CozeWebSDK.WebChatClient({
      config: {
        type: 'bot',
        bot_id: COZE_BOT_ID,
        isIframe: true
      },
      auth: {
        type: 'token',
        token: cozeToken,
        onRefreshToken: async function () {
          return fetchCozeToken()
        }
      },
      userInfo: {
        id: getCozeUserId(),
        url: COZE_SDK_ICON_URL,
        nickname: '商户用户'
      },
      ui: {
        base: {
          icon: COZE_SDK_ICON_URL,
          layout: 'pc',
          lang: 'zh-CN',
          zIndex: 10000
        },
        asstBtn: {
          isNeed: false
        },
        header: {
          isShow: true,
          isNeedClose: true
        },
        chatBot: {
          title: 'AI服务助手',
          width: 460,
          isNeedAudio: false,
          isNeedFunctionCallMessage: false,
          isNeedAddNewConversation: false,
          onShow: function () {
            cozeChatOpen = true
            setFloatingButtonVisible(false)
          },
          onHide: function () {
            cozeChatOpen = false
            setFloatingButtonVisible(true)
          }
        },
        input: {
          isNeedAudio: false
        },
        footer: {
          isShow: false
        }
      }
    })
    cozeBoundAccountKey = getCozeAccountKey()
  } catch (error) {
    void error
    return
  }

  hideSdkFloatingButton()
  setTimeout(hideSdkFloatingButton, 300)
  setTimeout(patchCozeIframes, 0)
  setTimeout(patchCozeIframes, 500)
  ensureFloatingButton()
}

function loadCozeSDK () {
  if (cozeChatClient || cozeSdkLoading) return Promise.resolve()
  if (!getMerchantJwt()) return Promise.resolve()

  patchCozeSdkCompat()

  if (window.CozeWebSDK) {
    return initCozeChatClient()
  }

  cozeSdkLoading = true
  return new Promise(function (resolve) {
    const existed = document.querySelector('script[data-coze-sdk="1"]')
    const onReady = function () {
      cozeSdkLoading = false
      initCozeChatClient().then(resolve)
    }

    if (existed) {
      if (existed.getAttribute('data-loaded') === '1') {
        onReady()
      } else {
        existed.addEventListener('load', onReady)
      }
      return
    }

    const script = document.createElement('script')
    script.src = COZE_SDK_URL
    script.setAttribute('data-coze-sdk', '1')
    script.onload = function () {
      script.setAttribute('data-loaded', '1')
      onReady()
    }
    script.onerror = function () {
      cozeSdkLoading = false
      resolve()
    }
    document.body.appendChild(script)
  })
}

function scheduleBootstrap () {
  if (bootstrapScheduled) return
  if (!getMerchantJwt()) return
  if (cozeChatClient && !shouldReinitCozeClient()) return
  if (shouldReinitCozeClient()) {
    destroyCozeChatClient()
  }
  bootstrapScheduled = true
  setTimeout(function () {
    loadCozeSDK().finally(function () {
      bootstrapScheduled = false
    })
  }, 300)
}

router.afterEach(function (to) {
  if (to.path === '/login') {
    destroyCozeChatClient()
    removeFloatingButton()
    return
  }
  if (shouldReinitCozeClient()) {
    destroyCozeChatClient()
  }
  ensureFloatingButton()
  scheduleBootstrap()
})

patchCozeSdkCompat()

if (getMerchantJwt()) {
  ensureFloatingButton()
  if (document.readyState === 'complete') {
    scheduleBootstrap()
  } else {
    window.addEventListener('load', scheduleBootstrap)
  }
}



// WEBPACK FOOTER //
// ./src/utils/coze-chat.js